import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { E6bSide, E6bWindScale, FlightComputerSession } from './flight-computer.session';
import {
  E6B_SLIDE_MS,
  E6B_TURN_MS,
  E6bCalibrator,
  E6bMode,
  E6bPlaybackController,
  E6bStepInfo,
  E6bWheelDriver,
  PlaybackMark,
} from './e6b-playback';

export type FlightComputerUiMode = 'interactive' | 'playback';

/**
 * Pencil mark geometry. The arms stop short of the centre so the speed arc and
 * the drift line stay visible through the point being read; MARK_GAP has to
 * clear the white halo, or the halo closes the opening back up.
 */
const MARK_RING = 5.5;
const MARK_ARM = 10;
const MARK_GAP = 4;
const MARK_HALO = 2.6;
const MARK_INK = 1.2;

/**
 * Lines are construction aids rather than readings, so more than one is
 * legitimate: multi-drift wind finding takes the intersection of two or three.
 * Past that they bury the card, so the oldest drops off.
 */
const MAX_LINES = 3;

type Tool = 'navigate' | 'dot' | 'line';

interface MarkDot {
  id: number;
  kind: 'dot';
  x: number;
  y: number;
  label?: string;
  ring?: 'inner' | 'outer';
}

interface MarkLine {
  id: number;
  kind: 'line';
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  ring?: 'inner' | 'outer';
}

type Mark = MarkDot | MarkLine;

interface SideState {
  rotation: number;
  slideY: number;
  /** Screen-space pan of the face (px); used when zoomed so edges stay reachable. */
  panX: number;
  panY: number;
  marks: Mark[];
}

@Component({
  selector: 'app-flight-computer',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './flight-computer.component.html',
  styleUrl: './flight-computer.component.css',
})
export class FlightComputerComponent
  implements AfterViewInit, OnChanges, OnDestroy, E6bWheelDriver
{
  /** Interactive tools vs explanation playback (read-only). */
  @Input() uiMode: FlightComputerUiMode = 'interactive';
  /** Instruction list for playback mode (ignored when interactive). */
  @Input() instructions: unknown[] | null = null;
  @Input() autoplay = true;

  @Output() closed = new EventEmitter<void>();
  @ViewChild('svgHost') svgHost!: ElementRef<HTMLDivElement>;

  tool: Tool = 'navigate';
  loading = true;
  loadError: string | null = null;
  lineStart: { x: number; y: number } | null = null;
  /** ATPLQ ships separate e6-b-wind.svg / e6-b-wind-high.svg cards. */
  readonly highScaleAvailable = true;
  showShortcuts = false;
  /** Fine pointer + hover ≈ keyboard/desktop; hide shortcut chrome on phones. */
  readonly keyboardAvailable =
    typeof window !== 'undefined' &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  captionText = '';
  playbackMeta = '—';
  playbackPlaying = false;

  private nextMarkId = 1;
  private dragMode: 'none' | 'rotate' | 'slide' | 'pan' = 'none';
  private lastAngle = 0;
  private lastPointerY = 0;
  private lastPointerX = 0;
  private markHistory: Record<E6bSide, Mark[][]> = { calculator: [], wind: [] };
  private loadToken = 0;
  private destroyed = false;
  private svgEl: SVGSVGElement | null = null;
  private rotateTarget: SVGGElement | null = null;
  private slideTarget: SVGGElement | null = null;
  private marksLayer: SVGGElement | null = null;
  /** Fixed (non-rotating) marks for outer-ring values on the calculator. */
  private fixedMarksLayer: SVGGElement | null = null;
  private hlRootLayer: SVGGElement | null = null;
  private highlightTargets: unknown[] = [];
  private _wheelCx = 255;
  private _wheelCy = 500;
  private activePointerId: number | null = null;
  private playback: E6bPlaybackController | null = null;
  private sessionSnapshot: {
    side: E6bSide;
    scale: E6bWindScale;
    zoomBySide: Record<E6bSide, number>;
  } | null = null;
  private animRaf: number | null = null;
  private animAbort = false;
  private viewReady = false;
  private silentUpdate = false;
  /** Absolute angles (deg, 0 = top, clockwise) of fixed outer-ring ticks for snap/nudge. */
  private outerTickAngles: number[] = [];
  /** Local dial/compass tick angles used with outer ticks for calculator snap lattice. */
  private dialTickAngles: number[] = [];
  /** Added to user rotation so calculator rings mesh at 0. */
  private dialAlignOffset = 0;

  private states: Record<E6bSide, SideState> = {
    calculator: { rotation: 0, slideY: 0, panX: 0, panY: 0, marks: [] },
    wind: { rotation: 0, slideY: 0, panX: 0, panY: 0, marks: [] },
  };

  constructor(
    private session: FlightComputerSession,
    private cdr: ChangeDetectorRef
  ) {
    if (!this.highScaleAvailable && this.session.scale === 'high') {
      this.session.scale = 'low';
    }
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    if (this.isPlayback) {
      void this.startPlayback();
    } else {
      void this.loadSide();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.viewReady) return;
    if (changes['uiMode'] || changes['instructions']) {
      if (this.isPlayback) {
        void this.startPlayback();
      } else if (changes['uiMode'] && !this.isPlayback) {
        this.stopPlayback();
        void this.loadSide();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.loadToken++;
    this.stopPlayback();
    this.cancelAnimations();
    this.restoreSessionSnapshot();
  }

  get isPlayback(): boolean {
    return this.uiMode === 'playback';
  }

  get mode(): E6bMode {
    if (this.side === 'calculator') return 'calculator';
    return this.scale === 'high' ? 'wind|high' : 'wind|low';
  }

  get wheelCx(): number {
    return this._wheelCx;
  }

  get wheelCy(): number {
    return this._wheelCy;
  }

  get side(): E6bSide {
    return this.session.side;
  }

  get scale(): E6bWindScale {
    return this.session.scale;
  }

  get zoom(): number {
    return this.session.zoom;
  }

  get rotation(): number {
    return this.states[this.side].rotation;
  }
  set rotation(v: number) {
    this.states[this.side].rotation = v;
  }

  get slideY(): number {
    return this.states[this.side].slideY;
  }
  set slideY(v: number) {
    this.states[this.side].slideY = v;
  }

  get marks(): Mark[] {
    return this.states[this.side].marks;
  }

  get panX(): number {
    return this.states[this.side].panX;
  }
  set panX(v: number) {
    this.states[this.side].panX = v;
  }

  get panY(): number {
    return this.states[this.side].panY;
  }
  set panY(v: number) {
    this.states[this.side].panY = v;
  }

  get canUndo(): boolean {
    return this.markHistory[this.side].length > 0;
  }

  /** Fine pointer ≈ desktop; coarse/touch gets background pan on the calculator face. */
  private get allowBackgroundPan(): boolean {
    return !this.keyboardAvailable;
  }

  close(): void {
    this.stopPlayback();
    this.restoreSessionSnapshot();
    this.closed.emit();
  }

  setSide(side: E6bSide): void {
    if (this.session.side === side) return;
    this.session.side = side;
    this.tool = 'navigate';
    this.lineStart = null;
    this.dragMode = 'none';
    this.loadSide();
  }

  toggleSide(): void {
    this.setSide(this.side === 'wind' ? 'calculator' : 'wind');
  }

  setScale(scale: E6bWindScale): void {
    if (scale === 'high' && !this.highScaleAvailable) return;
    if (this.session.scale === scale) return;
    this.session.scale = scale;
    if (this.side === 'wind') void this.loadSide();
  }

  toggleScale(): void {
    this.setScale(this.scale === 'low' ? 'high' : 'low');
  }

  setTool(tool: Tool): void {
    if (this.side !== 'wind' && (tool === 'dot' || tool === 'line')) return;
    this.tool = this.tool === tool ? 'navigate' : tool;
    this.lineStart = null;
    this.redrawMarks();
  }

  toggleShortcuts(): void {
    this.showShortcuts = !this.showShortcuts;
  }

  zoomIn(): void {
    this.session.zoom = Math.min(2.5, +(this.session.zoom + 0.15).toFixed(2));
    this.applyViewport();
  }

  zoomOut(): void {
    this.session.zoom = Math.max(0.55, +(this.session.zoom - 0.15).toFixed(2));
    this.applyViewport();
  }

  undo(): void {
    const prev = this.markHistory[this.side].pop();
    if (!prev) return;
    this.states[this.side].marks = prev.map((m) => ({ ...m }));
    this.lineStart = null;
    this.tool = 'navigate';
    this.redrawMarks();
  }

  reset(): void {
    this.pushMarkHistory();
    this.states[this.side] = { rotation: 0, slideY: 0, panX: 0, panY: 0, marks: [] };
    this.lineStart = null;
    this.tool = 'navigate';
    this.applyTransforms();
    this.redrawMarks();
  }

  onPointerDown(event: PointerEvent): void {
    if (this.isPlayback) return;
    if (!this.svgEl || (event.button !== undefined && event.button !== 0)) return;
    const pt = this.svgPoint(event);
    if (!pt) return;

    this.activePointerId = event.pointerId;
    try {
      (event.currentTarget as Element).setPointerCapture(event.pointerId);
    } catch {}
    event.preventDefault();

    if (this.side === 'wind' && this.tool === 'dot') {
      const local = this.toWheelLocal(pt.x, pt.y);
      this.pushMarkHistory();
      // Every wind-face problem reads one point, so marking again moves the
      // mark rather than leaving two red crosses to pick between. Undo still
      // brings the previous position back. Playback sets its marks directly
      // and is not affected; the calculator side pairs marks across rings.
      const state = this.states[this.side];
      state.marks = state.marks.filter((m) => m.kind !== 'dot');
      state.marks.push({ id: this.nextMarkId++, kind: 'dot', x: local.x, y: local.y });
      this.tool = 'navigate';
      this.lineStart = null;
      this.redrawMarks();
      return;
    }

    if (this.side === 'wind' && this.tool === 'line') {
      const local = this.toWheelLocal(pt.x, pt.y);
      if (!this.lineStart) {
        this.lineStart = local;
        this.redrawMarks();
      } else {
        this.pushMarkHistory();
        const state = this.states[this.side];
        const lines = state.marks.filter((m) => m.kind === 'line');
        if (lines.length >= MAX_LINES) {
          const oldest = lines[0].id;
          state.marks = state.marks.filter((m) => m.id !== oldest);
        }
        state.marks.push({
          id: this.nextMarkId++,
          kind: 'line',
          x1: this.lineStart.x,
          y1: this.lineStart.y,
          x2: local.x,
          y2: local.y,
        });
        this.lineStart = null;
        this.tool = 'navigate';
        this.redrawMarks();
      }
      return;
    }

    const dist = Math.hypot(pt.x - this._wheelCx, pt.y - this._wheelCy);
    const r = this.wheelRadius();

    if (this.side === 'calculator') {
      // On mobile, drag the grey background to pan so zoomed-out edges stay readable.
      if (this.allowBackgroundPan && dist > r + 28) {
        this.dragMode = 'pan';
        this.lastPointerX = event.clientX;
        this.lastPointerY = event.clientY;
      } else {
        this.dragMode = 'rotate';
        this.lastAngle = this.angle(pt.x, pt.y);
      }
      return;
    }

    if (dist >= r * 0.45 && dist <= r + 40) {
      this.dragMode = 'rotate';
      this.lastAngle = this.angle(pt.x, pt.y);
    } else {
      this.dragMode = 'slide';
      this.lastPointerY = event.clientY;
    }
  }

  onPointerMove(event: PointerEvent): void {
    if (this.isPlayback) return;
    if (this.dragMode === 'none') return;
    if (this.activePointerId !== null && event.pointerId !== this.activePointerId) return;

    if (this.dragMode === 'rotate') {
      const pt = this.svgPoint(event);
      if (!pt) return;
      const ang = this.angle(pt.x, pt.y);
      let delta = ang - this.lastAngle;
      if (delta > 180) delta -= 360;
      if (delta < -180) delta += 360;
      this.rotation = this.normDeg(this.rotation + delta);
      this.lastAngle = ang;
      this.applyRotate();
      return;
    }

    if (this.dragMode === 'pan') {
      const dx = event.clientX - this.lastPointerX;
      const dy = event.clientY - this.lastPointerY;
      this.panX = Math.max(-480, Math.min(480, this.panX + dx));
      this.panY = Math.max(-640, Math.min(640, this.panY + dy));
      this.lastPointerX = event.clientX;
      this.lastPointerY = event.clientY;
      this.applyViewport();
      return;
    }

    const dy = event.clientY - this.lastPointerY;
    this.slideY = Math.max(-420, Math.min(420, this.slideY + dy));
    this.lastPointerY = event.clientY;
    this.applySlide();
  }

  onPointerUp(event: PointerEvent): void {
    if (this.activePointerId !== null && event.pointerId !== this.activePointerId) return;
    try {
      (event.currentTarget as Element).releasePointerCapture?.(event.pointerId);
    } catch {}
    this.activePointerId = null;
    this.dragMode = 'none';
  }

  @HostListener('window:keydown', ['$event'])
  onKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      if (!this.isPlayback && this.showShortcuts) {
        this.showShortcuts = false;
        return;
      }
      this.close();
      return;
    }

    if (this.isPlayback || !this.keyboardAvailable) return;
    const t = event.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;

    // M arms the mark, L the line. Both toggle, so the same key cancels, and
    // both are silent on the calculator side, where the buttons are disabled.
    if (!event.ctrlKey && !event.metaKey && !event.altKey) {
      const letter = event.key.toLowerCase();
      if (letter === 'm' || letter === 'l') {
        if (this.side !== 'wind') return;
        event.preventDefault();
        this.setTool(letter === 'm' ? 'dot' : 'line');
        return;
      }
    }

    // +/- rotate by one tick; Shift (or DE Shift++ → *) steps five ticks
    const key = event.key;
    const code = event.code;
    const isPlus =
      key === '+' || key === '=' || key === '*' || code === 'NumpadAdd' || code === 'Equal';
    const isMinus =
      key === '-' || key === '_' || key === '–' || code === 'NumpadSubtract' || code === 'Minus';
    if (isPlus || isMinus) {
      event.preventDefault();
      const coarse = event.shiftKey || key === '*';
      const steps = (coarse ? 5 : 1) * (isMinus ? -1 : 1);
      this.nudgeRotationByTicks(steps);
    }
  }

  /** Step rotation so ticks mesh; one step = one outer-ring tick under the current match. */
  private nudgeRotationByTicks(steps: number): void {
    if (!steps) return;
    if (this.side === 'calculator' && this.outerTickAngles.length > 1) {
      this.rotation = this.nextTickAlignedRotation(this.rotation, steps);
    } else if (this.side === 'wind' && this.outerTickAngles.length > 1) {
      // Wind compass is degree-marked; step by measured tick spacing near current heading.
      this.rotation = this.nextTickAlignedRotation(this.rotation, steps);
    } else {
      this.rotation = this.normDeg(this.rotation + steps);
    }
    this.applyRotate();
  }

  private nextTickAlignedRotation(current: number, steps: number): number {
    const lattice = this.tickAlignmentLattices();
    if (lattice.length < 2) return this.normDeg(current + steps);

    const cur = this.normDeg(current);
    // Snap to nearest lattice angle first, then move `steps` lattice slots.
    let bestIdx = 0;
    let bestDist = 180;
    for (let i = 0; i < lattice.length; i++) {
      const d = Math.abs(this.normDelta(lattice[i] - cur));
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    const idx = (bestIdx + steps) % lattice.length;
    return lattice[idx < 0 ? idx + lattice.length : idx];
  }

  /**
   * Rotations (user space) where a graticule tick lines up under the index / outer ring.
   * Wind: exact 1° compass detents (same visual lines you see while turning).
   * Calculator: measured outer-ring tick spacings from the aligned zero.
   */
  private tickAlignmentLattices(): number[] {
    if (this.side === 'wind') {
      // compass-radians is a clean 360×1° ring; keep phase from the measured 0° tick if present.
      const outer = this.outerTickAngles;
      let phase = 0;
      if (outer.length) {
        // Nearest integer-degree phase so clicks sit on the drawn marks.
        let best = outer[0];
        let bestAbs = Math.abs(this.normDelta(best));
        for (const a of outer) {
          const d = Math.abs(this.normDelta(a));
          if (d < bestAbs) {
            bestAbs = d;
            best = a;
          }
        }
        phase = this.normDelta(best);
      }
      return Array.from({ length: 360 }, (_, i) => this.normDeg(i + phase));
    }
    const outer = this.outerTickAngles;
    if (outer.length < 2) return [];
    const base = outer[0];
    // Quantize lightly to kill SVG sub-degree noise while preserving log-scale spacing.
    const rel = outer.map((a) => this.normDeg(a - base));
    return this.uniqueSortedAngles(
      rel.map((a) => Math.round(a * 4) / 4),
      0.2
    );
  }

  private uniqueSortedAngles(angles: number[], tol = 0.05): number[] {
    const sorted = angles.map((a) => this.normDeg(a)).sort((a, b) => a - b);
    const out: number[] = [];
    for (const a of sorted) {
      if (!out.length || Math.abs(this.normDelta(a - out[out.length - 1])) > tol) {
        out.push(a);
      }
    }
    // Also ensure wrap gap is respected by including 0-equivalent once.
    return out;
  }

  private normDeg(deg: number): number {
    const d = deg % 360;
    return d < 0 ? d + 360 : d;
  }

  private normDelta(deg: number): number {
    const d = this.normDeg(deg + 180) - 180;
    return d;
  }

  private assetUrl(file: string): string {
    const base = document.querySelector('base')?.href || document.baseURI || '/';
    return new URL(`flight-computer/${file}`, base).toString();
  }

  private async loadSide(): Promise<void> {
    const token = ++this.loadToken;
    // Swapping cards keeps the old one up until the new one is ready, so the
    // status line is only for the first load, when there is nothing to show.
    this.loading = !this.svgEl;
    this.loadError = null;
    this.svgEl = null;
    this.rotateTarget = null;
    this.slideTarget = null;
    this.marksLayer = null;
    this.fixedMarksLayer = null;
    this.hlRootLayer = null;
    this.cdr.detectChanges();

    const file =
      this.side === 'calculator'
        ? 'calculator.svg'
        : this.scale === 'high'
          ? 'wind-high.svg'
          : 'wind-low.svg';

    try {
      const url = `${this.assetUrl(file)}?v=${encodeURIComponent(this.side + '-' + this.scale + '-st11ink')}`;
      const res = await fetch(url, { credentials: 'same-origin', cache: 'no-cache' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const svg = await res.text();
      if (this.destroyed || token !== this.loadToken) return;
      this.loading = false;
      this.cdr.detectChanges();
      const el = this.svgHost?.nativeElement;
      if (!el) {
        this.loadError = 'E6B host missing.';
        this.cdr.detectChanges();
        return;
      }
      el.innerHTML = svg.replace(/<script[\s\S]*?<\/script>/gi, '');
      // Bind in the same task as the injection. A raw card carries no width or
      // height of its own, so it lays out at the host width by its 660:1000
      // ratio, three and a half times too tall, and the artwork is scaled to
      // fill that. Leave a frame in here and the swap flashes an oversized,
      // unrotated instrument before the transforms land.
      this.bindSvg();
    } catch {
      if (this.destroyed || token !== this.loadToken) return;
      const el = this.svgHost?.nativeElement;
      if (el) el.innerHTML = '';
      this.loading = false;
      this.loadError = 'Could not load E6B graphics.';
      this.cdr.detectChanges();
    }
  }

  private bindSvg(): void {
    const host = this.svgHost?.nativeElement;
    if (!host) return;
    const svg = host.querySelector('svg');
    if (!svg) {
      this.loadError = 'E6B SVG failed to mount.';
      this.cdr.detectChanges();
      return;
    }

    this.svgEl = svg as SVGSVGElement;
    this.svgEl.style.width = '100%';
    this.svgEl.style.height = '100%';
    this.svgEl.style.display = 'block';
    this.svgEl.style.pointerEvents = 'none';
    this.svgEl.style.overflow = 'visible';
    this.svgEl.setAttribute('overflow', 'visible');
    this.svgEl.setAttribute('preserveAspectRatio', 'xMidYMid meet');

    if (this.side === 'wind') {
      this.rotateTarget =
        (svg.querySelector('#plot-group') as SVGGElement) ||
        (svg.querySelector('#compass') as SVGGElement);
      this.slideTarget = svg.querySelector('#tas-arc') as SVGGElement;
      this.setCenter(svg.querySelector('#compass-ring') as SVGGraphicsElement | null, 255, 500);
      // Degree graticule on the rotating compass (1° ring), not the sparse true-index ticks.
      this.captureTickAngles(
        (svg.querySelector('#compass-radians') as SVGGElement | null) ||
          (svg.querySelector('#one-degree') as SVGGElement | null) ||
          (svg.querySelector('#true-index-tick-marks') as SVGGElement | null),
        null
      );
      this.dialAlignOffset = 0;
    } else {
      this.rotateTarget = svg.querySelector('#tas-dial') as SVGGElement;
      this.slideTarget = null;
      // Geometric tick center (bbox of dial includes windows and skews the pivot).
      this._wheelCx = 256.004;
      this._wheelCy = 295.702;
      this.captureTickAngles(
        svg.querySelector('#Tick_Marks_-_Outer_Ring') as SVGGElement | null,
        svg.querySelector('#Tick_Marks-4 #Outer') as SVGGElement | null
      );
      // Playback uses log-scale calibrator angles; tick-mesh offset would shift oppose/read.
      this.dialAlignOffset = this.isPlayback ? 0 : this.computeDialAlignOffset();
      // Drop any Illustrator bake matrix; rotation is applied exclusively via applyRotate.
      this.rotateTarget?.removeAttribute('transform');
    }

    const parent = this.rotateTarget || svg;
    let layer = parent.querySelector('#qtrainer-marks') as SVGGElement | null;
    if (!layer) {
      layer = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      layer.setAttribute('id', 'qtrainer-marks');
      parent.appendChild(layer);
    }
    this.marksLayer = layer;

    let fixedLayer = svg.querySelector('#qtrainer-fixed-marks') as SVGGElement | null;
    if (!fixedLayer) {
      fixedLayer = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      fixedLayer.setAttribute('id', 'qtrainer-fixed-marks');
      svg.appendChild(fixedLayer);
    }
    this.fixedMarksLayer = fixedLayer;

    let hlRoot = svg.querySelector('#qtrainer-hl-root') as SVGGElement | null;
    if (!hlRoot) {
      hlRoot = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      hlRoot.setAttribute('id', 'qtrainer-hl-root');
      svg.appendChild(hlRoot);
    }
    this.hlRootLayer = hlRoot;

    this.applyTransforms();
    this.redrawMarks();
    if (this.side === 'wind') {
      requestAnimationFrame(() => this.autoFitWindZoomOnce());
    }
  }

  /** Fit wind face into the canvas. Playback always refits; interactive only once. */
  private autoFitWindZoomOnce(force = false): void {
    const FIT_VERSION = 3;
    if (
      (!force && this.session.windAutoZoomVersion >= FIT_VERSION) ||
      this.side !== 'wind' ||
      this.destroyed
    ) {
      return;
    }
    const host = this.svgHost?.nativeElement;
    // Fit the white outer plate (true-index), not the smaller black compass.
    const ring =
      (this.svgEl?.querySelector('#true-index-ring') as SVGGraphicsElement | null) ||
      (this.svgEl?.querySelector('#true-index') as SVGGraphicsElement | null) ||
      (this.svgEl?.querySelector('#compass-ring') as SVGGraphicsElement | null);
    if (!host || !ring) {
      if (!force) this.session.windAutoZoomVersion = FIT_VERSION;
      return;
    }
    try {
      host.style.transform = 'translate(0px, 0px) scale(1)';
      const hostRect = host.getBoundingClientRect();
      const ringRect = ring.getBoundingClientRect();

      if (hostRect.width > 0 && hostRect.height > 0 && ringRect.width > 0 && ringRect.height > 0) {
        const fitW = (hostRect.width * 0.98) / ringRect.width;
        const fitH = (hostRect.height * 0.96) / ringRect.height;
        const fitted = Math.min(2.8, Math.max(0.55, +Math.min(fitW, fitH).toFixed(2)));
        this.session.zoom = fitted;
      }
    } catch {
      /* keep default zoom */
    }
    if (!force) this.session.windAutoZoomVersion = FIT_VERSION;
    this.applyViewport();
  }

  private captureTickAngles(outerGroup: SVGGElement | null, dialGroup: SVGGElement | null): void {
    this.outerTickAngles = this.lineAnglesInGroup(outerGroup, this._wheelCx, this._wheelCy);
    this.dialTickAngles = this.lineAnglesInGroup(dialGroup, this._wheelCx, this._wheelCy);
  }

  private lineAnglesInGroup(
    group: SVGGElement | null,
    cx: number,
    cy: number
  ): number[] {
    if (!group) return [];
    const angles: number[] = [];
    const lines = group.querySelectorAll('line');
    lines.forEach((line) => {
      const x1 = parseFloat(line.getAttribute('x1') || '');
      const y1 = parseFloat(line.getAttribute('y1') || '');
      const x2 = parseFloat(line.getAttribute('x2') || '');
      const y2 = parseFloat(line.getAttribute('y2') || '');
      if (![x1, y1, x2, y2].every(Number.isFinite)) return;
      const mx = (x1 + x2) / 2;
      const my = (y1 + y2) / 2;
      // 0° at top, clockwise positive (SVG y grows downward).
      const deg = (Math.atan2(mx - cx, -(my - cy)) * 180) / Math.PI;
      angles.push(this.normDeg(deg));
    });
    return this.uniqueSortedAngles(angles, 0.04);
  }

  /** Small constant rotation so dial Outer ticks mesh with the fixed outer ring at user 0. */
  private computeDialAlignOffset(): number {
    const outer = this.outerTickAngles;
    const dial = this.dialTickAngles;
    if (!outer.length || !dial.length) return 0;
    const samples = Math.min(outer.length, 64);
    let sum = 0;
    let n = 0;
    for (let i = 0; i < samples; i++) {
      const o = outer[Math.floor((i * outer.length) / samples)];
      let best = 180;
      for (const d of dial) {
        const delta = this.normDelta(d - o);
        if (Math.abs(delta) < Math.abs(best)) best = delta;
      }
      if (Math.abs(best) < 2) {
        // dial − outer; rotate dial by −that to align.
        sum += best;
        n++;
      }
    }
    return n ? -sum / n : 0;
  }

  private setCenter(el: SVGGraphicsElement | null, fx: number, fy: number): void {
    this._wheelCx = fx;
    this._wheelCy = fy;
    if (!el) return;
    try {
      const bb = el.getBBox();
      if (bb.width > 0 && bb.height > 0) {
        this._wheelCx = bb.x + bb.width / 2;
        this._wheelCy = bb.y + bb.height / 2;
      }
    } catch {}
  }

  private wheelRadius(): number {
    if (this.side === 'calculator') return 180;
    try {
      const ring = this.svgEl?.querySelector('#compass-ring') as SVGGraphicsElement | null;
      if (ring) {
        const bb = ring.getBBox();
        return Math.min(bb.width, bb.height) / 2;
      }
    } catch {}
    return 250;
  }

  private applyTransforms(): void {
    this.applyRotate();
    this.applySlide();
    this.applyViewport();
  }

  private applyRotate(): void {
    if (this.silentUpdate) return;
    const angle = this.rotation + this.dialAlignOffset;
    this.rotateTarget?.setAttribute(
      'transform',
      `rotate(${angle} ${this._wheelCx} ${this._wheelCy})`
    );
    this.redrawMarks();
  }

  private applySlide(): void {
    if (this.silentUpdate) return;
    this.slideTarget?.setAttribute('transform', `translate(0 ${this.slideY})`);
  }

  private applyViewport(): void {
    const host = this.svgHost?.nativeElement;
    if (!host) return;
    host.style.transform = `translate(${this.panX}px, ${this.panY}px) scale(${this.zoom})`;
    host.style.transformOrigin = 'center center';
  }

  private redrawMarks(): void {
    if (this.silentUpdate) return;
    if (!this.marksLayer) return;
    this.marksLayer.replaceChildren();
    if (this.fixedMarksLayer) this.fixedMarksLayer.replaceChildren();
    for (const m of this.marks) {
      const ring = m.ring === 'outer' ? 'outer' : 'inner';
      const parent = ring === 'outer' ? this.fixedMarksLayer : this.marksLayer;
      if (!parent) continue;
      if (m.kind === 'dot') {
        // A filled disc covers 3 kt on the low card and 17 kt on the high one,
        // and hides the arc and drift line it has to be read against. Draw an
        // open ring with a cross instead: the crossing point is the reading.
        const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        // Marks ride the rotating disc. Hold the glyph square to the screen so
        // the arms stay at 45 deg to the arcs and never lie along one.
        if (ring === 'inner') {
          const angle = this.rotation + this.dialAlignOffset;
          g.setAttribute('transform', `rotate(${-angle} ${m.x} ${m.y})`);
        }
        const a = MARK_ARM * Math.SQRT1_2;
        const g0 = MARK_GAP * Math.SQRT1_2;
        const cross =
          `M ${m.x - a} ${m.y - a} L ${m.x - g0} ${m.y - g0} ` +
          `M ${m.x + g0} ${m.y + g0} L ${m.x + a} ${m.y + a} ` +
          `M ${m.x - a} ${m.y + a} L ${m.x - g0} ${m.y + g0} ` +
          `M ${m.x + g0} ${m.y - g0} L ${m.x + a} ${m.y - a}`;
        // Halo first, so the mark stays legible over the printed card.
        for (const [colour, width] of [['#fff8e7', MARK_HALO], ['#c1121f', MARK_INK]] as const) {
          const o = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          o.setAttribute('cx', String(m.x));
          o.setAttribute('cy', String(m.y));
          o.setAttribute('r', String(MARK_RING));
          o.setAttribute('fill', 'none');
          o.setAttribute('stroke', colour);
          o.setAttribute('stroke-width', String(width));
          g.appendChild(o);
          const p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          p.setAttribute('d', cross);
          p.setAttribute('fill', 'none');
          p.setAttribute('stroke', colour);
          p.setAttribute('stroke-width', String(width));
          p.setAttribute('stroke-linecap', 'butt');
          g.appendChild(p);
        }
        parent.appendChild(g);
        if (m.label) {
          // Offset label along the radial so it sits clear of the tick.
          const dx = m.x - this._wheelCx;
          const dy = m.y - this._wheelCy;
          const len = Math.hypot(dx, dy) || 1;
          const out = ring === 'outer' ? 1 : -1;
          const lx = m.x + (dx / len) * 18 * out;
          const ly = m.y + (dy / len) * 18 * out;
          const t = document.createElementNS('http://www.w3.org/2000/svg', 'text');
          t.setAttribute('x', String(lx));
          t.setAttribute('y', String(ly + 4));
          t.setAttribute('text-anchor', 'middle');
          t.setAttribute('fill', '#c1121f');
          t.setAttribute('stroke', '#fff8e7');
          t.setAttribute('stroke-width', '3.5');
          t.setAttribute('paint-order', 'stroke');
          t.setAttribute('font-size', '15');
          t.setAttribute('font-weight', '800');
          t.setAttribute('font-family', 'ui-sans-serif, system-ui, sans-serif');
          t.textContent = m.label;
          // Inner marks live on the rotating dial. Counter-rotate the glyphs
          // so both rings read upright (horizontal), same as the outer labels.
          if (ring === 'inner') {
            const angle = this.rotation + this.dialAlignOffset;
            t.setAttribute('transform', `rotate(${-angle} ${lx} ${ly + 4})`);
          }
          parent.appendChild(t);
        }
      } else {
        const l = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        l.setAttribute('x1', String(m.x1));
        l.setAttribute('y1', String(m.y1));
        l.setAttribute('x2', String(m.x2));
        l.setAttribute('y2', String(m.y2));
        l.setAttribute('stroke', '#c1121f');
        l.setAttribute('stroke-width', '2.2');
        parent.appendChild(l);
      }
    }
    if (this.lineStart) {
      const c = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      c.setAttribute('cx', String(this.lineStart.x));
      c.setAttribute('cy', String(this.lineStart.y));
      c.setAttribute('r', '3.5');
      c.setAttribute('fill', 'none');
      c.setAttribute('stroke', '#c1121f');
      c.setAttribute('stroke-width', '1.5');
      c.setAttribute('stroke-dasharray', '3 2');
      this.marksLayer.appendChild(c);
    }
    this.redrawHighlights();
  }

  private redrawHighlights(): void {
    if (this.hlRootLayer) this.hlRootLayer.replaceChildren();
    if (this.marksLayer) {
      this.marksLayer.querySelectorAll('.e6b-svg-hl').forEach((n) => n.remove());
    }
    if (this.fixedMarksLayer) {
      this.fixedMarksLayer.querySelectorAll('.e6b-svg-hl').forEach((n) => n.remove());
    }
    if (!this.isPlayback) return;
    for (const t of this.highlightTargets) {
      const kind =
        typeof t === 'string' ? t : (t as { kind?: string; mark?: string; ring?: string })?.kind;
      if (kind === 'true_index') {
        const hl = E6bCalibrator.trueIndexHighlight;
        this.svgHlCircle(this.hlRootLayer, hl.x, hl.y, hl.r);
        continue;
      }
      if (kind === 'mach_index') {
        const hl = E6bCalibrator.machIndexHighlight;
        this.svgHlCircle(this.hlRootLayer, hl.x, hl.y, hl.r);
        continue;
      }
      if (kind === 'pressure_alt') {
        const pa = Number((t as { value?: number }).value);
        const hl = E6bCalibrator.calculatorPressureAltSpot(pa, this._wheelCx, this._wheelCy);
        if (hl) this.svgHlCircle(this.hlRootLayer, hl.x, hl.y, hl.r);
        continue;
      }
      if (kind === 'grommet') {
        // Wind face only — never draw a centre ring on the calculator.
        if (this.side === 'wind') {
          this.svgHlCircle(this.hlRootLayer, this._wheelCx, this._wheelCy, 12);
        }
        continue;
      }
      if (kind === 'mark') {
        if (this.side === 'wind') {
          const last = [...this.marks].reverse().find((m) => m.kind === 'dot');
          if (last) this.svgHlCircle(this.marksLayer, last.x, last.y, 12);
        }
        continue;
      }
      if (kind === 'unit' || kind === 'oppose') {
        const obj = typeof t === 'object' && t ? (t as {
          mark?: string;
          ring?: string;
          from?: string;
          to?: string;
        }) : {};
        if (kind === 'oppose' && obj.from && obj.to) {
          this.svgHlUnitSpot(obj.from, 'outer');
          this.svgHlUnitSpot(obj.to, 'inner');
          continue;
        }
        const mark = obj.mark || '';
        const ring = obj.ring === 'inner' ? 'inner' : 'outer';
        this.svgHlUnitSpot(mark, ring);
        continue;
      }
      const markName =
        typeof t === 'string'
          ? t
          : ((t as { mark?: string }).mark || kind || '');
      if (
        markName &&
        !['true_index', 'grommet', 'mark', 'ring', 'slider', 'unit', 'oppose', 'mach_index', 'pressure_alt'].includes(
          markName,
        )
      ) {
        this.svgHlUnitSpot(markName, 'outer');
      }
    }
  }

  private svgHlCircle(
    parent: SVGGElement | null,
    x: number,
    y: number,
    r: number,
  ): void {
    if (!parent) return;
    const c = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    c.setAttribute('class', 'e6b-svg-hl e6b-svg-hl--pulse');
    c.setAttribute('cx', String(x));
    c.setAttribute('cy', String(y));
    c.setAttribute('r', String(r));
    c.setAttribute('fill', 'none');
    c.setAttribute('stroke', '#c1121f');
    c.setAttribute('stroke-width', '2.2');
    parent.appendChild(c);
  }

  /** Red ring covering the printed unit word and its blue arrow. */
  private svgHlUnitSpot(mark: string, ring: 'inner' | 'outer'): void {
    const hl = E6bCalibrator.calculatorUnitHighlight(mark, ring, this._wheelCx, this._wheelCy);
    if (!hl) return;
    const parent = ring === 'outer' ? this.hlRootLayer : this.marksLayer;
    this.svgHlCircle(parent, hl.x, hl.y, hl.r);
  }

  private toWheelLocal(x: number, y: number) {
    const rad = (-this.rotation * Math.PI) / 180;
    const dx = x - this._wheelCx;
    const dy = y - this._wheelCy;
    return {
      x: this._wheelCx + dx * Math.cos(rad) - dy * Math.sin(rad),
      y: this._wheelCy + dx * Math.sin(rad) + dy * Math.cos(rad),
    };
  }

  private angle(x: number, y: number) {
    return (Math.atan2(y - this._wheelCy, x - this._wheelCx) * 180) / Math.PI + 90;
  }

  private svgPoint(event: PointerEvent) {
    if (!this.svgEl) return null;
    const ctm = this.svgEl.getScreenCTM();
    if (!ctm) return null;
    const pt = this.svgEl.createSVGPoint();
    pt.x = event.clientX;
    pt.y = event.clientY;
    const local = pt.matrixTransform(ctm.inverse());
    return { x: local.x, y: local.y };
  }

  private pushMarkHistory(): void {
    this.markHistory[this.side].push(this.marks.map((m) => ({ ...m })));
    if (this.markHistory[this.side].length > 40) this.markHistory[this.side].shift();
  }

  // --- Playback mode (E6bWheelDriver) ---

  playbackPrev(): void {
    void this.playback?.prev().then(() => {
      this.playbackPlaying = false;
      this.cdr.detectChanges();
    });
  }

  playbackNext(): void {
    void this.playback?.next().then(() => {
      this.playbackPlaying = false;
      this.cdr.detectChanges();
    });
  }

  playbackReplay(): void {
    this.playbackPlaying = true;
    this.cdr.detectChanges();
    void this.playback?.replay().then(() => {
      this.playbackPlaying = false;
      this.cdr.detectChanges();
    });
  }

  playbackTogglePlay(): void {
    if (!this.playback) return;
    if (this.playback.playing) {
      this.playback.pause();
      this.playbackPlaying = false;
      this.cdr.detectChanges();
      return;
    }
    this.playbackPlaying = true;
    this.cdr.detectChanges();
    void this.playback.play().then(() => {
      this.playbackPlaying = false;
      this.cdr.detectChanges();
    });
  }

  async ensureMode(mode: E6bMode): Promise<void> {
    const side: E6bSide = mode === 'calculator' ? 'calculator' : 'wind';
    const scale: E6bWindScale = mode.endsWith('high') ? 'high' : 'low';
    const same =
      this.side === side &&
      (side === 'calculator' || this.scale === scale) &&
      !!this.svgEl;
    if (same) {
      if (this.isPlayback && side === 'wind' && !this.silentUpdate) {
        await this.fitPlaybackViewport();
      }
      return;
    }
    this.snapshotSessionOnce();
    this.session.side = side;
    if (side === 'wind') this.session.scale = scale;
    this.states[side].rotation = 0;
    this.states[side].slideY = 0;
    this.states[side].panX = 0;
    this.states[side].panY = 0;
    this.states[side].marks = [];
    this.highlightTargets = [];
    this.captionText = '';
    this.tool = 'navigate';
    this.lineStart = null;
    await this.loadSide();
    if (this.isPlayback && side === 'wind' && !this.silentUpdate) {
      await this.fitPlaybackViewport();
    }
  }

  async setRotation(deg: number, opts?: { instant?: boolean; speed?: number }): Promise<void> {
    if (opts?.instant) {
      this.states[this.side].rotation = deg;
      this.applyRotate();
      return;
    }
    const speed = Math.max(0.25, Number(opts?.speed) || 1);
    await this.animateRotation(deg, E6B_TURN_MS / speed);
  }

  async setSlideY(y: number, opts?: { instant?: boolean; speed?: number }): Promise<void> {
    if (opts?.instant) {
      this.states[this.side].slideY = y;
      this.applySlide();
      return;
    }
    const speed = Math.max(0.25, Number(opts?.speed) || 1);
    await this.animateSlide(y, E6B_SLIDE_MS / speed);
  }

  getMarks(): PlaybackMark[] {
    return this.marks.map((m) =>
      m.kind === 'dot'
        ? { kind: 'dot', x: m.x, y: m.y, label: m.label, ring: m.ring }
        : {
            kind: 'line',
            x1: m.x1,
            y1: m.y1,
            x2: m.x2,
            y2: m.y2,
            ring: m.ring,
          },
    );
  }

  setMarks(marks: PlaybackMark[]): void {
    this.states[this.side].marks = marks.map((m) =>
      m.kind === 'dot'
        ? {
            id: this.nextMarkId++,
            kind: 'dot' as const,
            x: m.x,
            y: m.y,
            label: m.label,
            ring: m.ring,
          }
        : {
            id: this.nextMarkId++,
            kind: 'line' as const,
            x1: m.x1,
            y1: m.y1,
            x2: m.x2,
            y2: m.y2,
            ring: m.ring,
          },
    );
    this.redrawMarks();
  }

  clearMarks(): void {
    this.states[this.side].marks = [];
    this.redrawMarks();
  }

  setCaption(text: string): void {
    this.captionText = (text || '').trim();
    if (!this.silentUpdate) this.cdr.detectChanges();
  }

  setHighlights(targets: unknown): void {
    this.highlightTargets = Array.isArray(targets) ? targets.slice() : [targets];
    if (!this.silentUpdate) this.redrawHighlights();
  }

  clearOverlays(): void {
    this.captionText = '';
    this.highlightTargets = [];
    if (!this.silentUpdate) {
      this.redrawHighlights();
      this.cdr.detectChanges();
    }
  }

  cancelAnimations(): void {
    this.animAbort = true;
    if (this.animRaf != null) {
      cancelAnimationFrame(this.animRaf);
      this.animRaf = null;
    }
  }

  beginSilentUpdate(): void {
    this.silentUpdate = true;
  }

  endSilentUpdate(): void {
    this.silentUpdate = false;
    this.applyTransforms();
    this.redrawMarks();
    this.redrawHighlights();
    this.cdr.detectChanges();
  }

  private async startPlayback(): Promise<void> {
    this.snapshotSessionOnce();
    this.stopPlayback(false);
    this.tool = 'navigate';
    this.lineStart = null;
    this.showShortcuts = false;
    // Match interactive framing: no leftover pan/zoom from a prior session.
    this.states.wind.panX = 0;
    this.states.wind.panY = 0;
    this.states.calculator.panX = 0;
    this.states.calculator.panY = 0;
    this.playback = new E6bPlaybackController(this, {
      onStep: (info: E6bStepInfo) => {
        const total = info.userTotal || 0;
        const n = info.userIndex || 0;
        this.playbackMeta = total ? `${n} / ${total}` : '—';
        this.playbackPlaying = !!this.playback?.playing;
        this.cdr.detectChanges();
      },
    });
    await this.playback.loadInstructions(this.instructions || []);
    await this.fitPlaybackViewport();
    this.playbackMeta =
      this.playback.userFacingCount() > 0
        ? `0 / ${this.playback.userFacingCount()}`
        : '—';
    this.cdr.detectChanges();
    if (this.autoplay && (this.instructions || []).length) {
      this.playbackPlaying = true;
      this.cdr.detectChanges();
      await this.playback.play();
      this.playbackPlaying = false;
      this.cdr.detectChanges();
    }
  }

  /** Playback uses the interactive FC canvas (22% wind pad + autofit), not the iframe player's loose 6% pad. */
  private async fitPlaybackViewport(): Promise<void> {
    if (!this.isPlayback || this.side !== 'wind' || this.destroyed) return;
    this.states[this.side].panX = 0;
    this.states[this.side].panY = 0;
    this.cdr.detectChanges();
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
    this.autoFitWindZoomOnce(true);
    // Second pass after toolbar/layout settles (modal size can change on first paint).
    await new Promise<void>((resolve) => {
      setTimeout(() => {
        if (!this.destroyed && this.isPlayback) this.autoFitWindZoomOnce(true);
        resolve();
      }, 50);
    });
  }

  private stopPlayback(resetMeta = true): void {
    this.playback?.destroy();
    this.playback = null;
    this.playbackPlaying = false;
    if (resetMeta) {
      this.playbackMeta = '—';
      this.captionText = '';
      this.highlightTargets = [];
    }
  }

  private snapshotSessionOnce(): void {
    if (!this.sessionSnapshot) {
      this.sessionSnapshot = {
        side: this.session.side,
        scale: this.session.scale,
        zoomBySide: {
          calculator: this.session.zoomBySide.calculator,
          wind: this.session.zoomBySide.wind,
        },
      };
    }
  }

  private restoreSessionSnapshot(): void {
    if (!this.sessionSnapshot) return;
    this.session.side = this.sessionSnapshot.side;
    this.session.scale = this.sessionSnapshot.scale;
    this.session.zoomBySide.calculator = this.sessionSnapshot.zoomBySide.calculator;
    this.session.zoomBySide.wind = this.sessionSnapshot.zoomBySide.wind;
    this.sessionSnapshot = null;
  }

  private shortestDelta(from: number, to: number): number {
    let d = to - from;
    while (d > 180) d -= 360;
    while (d < -180) d += 360;
    return d;
  }

  private easeInOut(t: number): number {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  private animateRotation(target: number, ms: number): Promise<void> {
    this.cancelAnimations();
    this.animAbort = false;
    const from = this.rotation;
    const delta = this.shortestDelta(from, target);
    if (Math.abs(delta) < 0.05) {
      this.states[this.side].rotation = target;
      this.applyRotate();
      return Promise.resolve();
    }
    const start = performance.now();
    return new Promise((resolve) => {
      const tick = (now: number) => {
        if (this.animAbort || this.destroyed) {
          resolve();
          return;
        }
        const t = Math.min(1, (now - start) / ms);
        this.states[this.side].rotation = from + delta * this.easeInOut(t);
        this.applyRotate();
        if (t < 1) {
          this.animRaf = requestAnimationFrame(tick);
        } else {
          this.states[this.side].rotation = target;
          this.applyRotate();
          this.animRaf = null;
          resolve();
        }
      };
      this.animRaf = requestAnimationFrame(tick);
    });
  }

  private animateSlide(target: number, ms: number): Promise<void> {
    this.cancelAnimations();
    this.animAbort = false;
    const from = this.slideY;
    const delta = target - from;
    if (Math.abs(delta) < 0.5) {
      this.states[this.side].slideY = target;
      this.applySlide();
      return Promise.resolve();
    }
    const start = performance.now();
    return new Promise((resolve) => {
      const tick = (now: number) => {
        if (this.animAbort || this.destroyed) {
          resolve();
          return;
        }
        const t = Math.min(1, (now - start) / ms);
        this.states[this.side].slideY = from + delta * this.easeInOut(t);
        this.applySlide();
        if (t < 1) {
          this.animRaf = requestAnimationFrame(tick);
        } else {
          this.states[this.side].slideY = target;
          this.applySlide();
          this.animRaf = null;
          resolve();
        }
      };
      this.animRaf = requestAnimationFrame(tick);
    });
  }
}
